import React, {
  Fragment,
  useEffect,
  useContext,
  useMemo,
  useState,
} from 'react'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import DashboardRouter from 'Config/Routing/DashboardRouter'
import HeaderPlatform from 'Containers/Header'
import SidebarPlatform from 'Containers/Sidebar'
import { UIBlock } from 'Containers/UIBlock'
import 'rc-calendar/assets/index.css'

import ReduxModal from '../../Components/ReduxModal/container/ReduxModal'
import { authorization } from '../../Module/Auth/Auth'
import { gtagWrapper } from '../../Utils/Functions'
import { SnackBarContext } from '../../Components/SnackBar'
import { If } from '../../Components/IfElse'
import { VerifyEmail } from '../../Components/Alerts'
import { EDDM_SIGNUP } from '../../shared/constants'
import HeaderSelfServe from '../HeaderSelfServe'
import SidebarSelfServe from '../SidebarSelfServe'
import { HeaderProvider } from '../HeaderSelfServe/HeaderContext'
import IframePreloader from '../../Components/IframePreloader'

import { sendClientVerificationByEmail, isClientEmailVerified } from './graphQL'
import { useWrappedPosthog } from '../../Components/PostHogProvider/hooks'
import { updateLoggedInUser } from '../../Reducers/Users/actions'
import ActionSheetNavbarSelfServe from '../ActionSheetNavbarSelfServe'
import useSmallScreen from '../../Components/ClientDashboard/Components/CampaignCard/useSmallScreen'
import { getClientById } from '../../Reducers/Clients/actions'

import { loadGTM } from '../../Utils/Analytics/gtmLoader'

const App = () => {
  const smallScreen = useSmallScreen()
  const dispatch = useDispatch()
  const history = useHistory()
  const match = useRouteMatch()
  const runSnackBar = useContext(SnackBarContext)
  const posthog = useWrappedPosthog()

  const [isOpenAssistant, setIsOpenAssistant] = useState(false)
  const [isHeaderVisible, setIsHeaderVisible] = useState(true)
  const {
    users: { loggedUser },
  } = useSelector(state => state)

  const toggleAssistant = () => {
    setIsOpenAssistant(prev => !prev)
  }

  const onClose = () => {
    setIsOpenAssistant(false)
  }

  const user = authorization.getUser()
  const {
    id: userId,
    client,
    firstName,
    lastName,
    countryName,
    email,
    role,
    mobilePhone,
    profilePicture,
  } = user

  const { isVerified = true } = useMemo(() => loggedUser || user, [
    loggedUser,
    user,
  ])

  const isNewUI = authorization.isClientRole()

  const isEDDMSignup = client && client.createdBy === EDDM_SIGNUP

  useEffect(() => {
    if (isEDDMSignup) {
      loadGTM('GTM-K535PLVV')
    } else {
      loadGTM('TVBX5KQ', 'https://sgtm.oppizi.com/pqtwjlgp.js?st=TVBX5KQ')
    }
  }, [isEDDMSignup])

  const sendVerificationEmail = async e => {
    if (e) e.preventDefault()
    try {
      const response = await sendClientVerificationByEmail()
      const {
        data: { sendClientVerificationEmail },
      } = await response.json()
      if (sendClientVerificationEmail.success) {
        runSnackBar({
          type: 'success',
          vertical: 'top',
          msg: 'Email sent successfully. Please check your mailbox.',
        })
        return true
      } else if (
        sendClientVerificationEmail.error ===
        'for the moment you are limited to this action'
      ) {
        runSnackBar({
          type: 'error',
          vertical: 'top',
          msg: 'Wait at least 2 minutes. You have already sent a request.',
        })
        return false
      } else {
        runSnackBar({
          type: 'error',
          vertical: 'top',
          msg: 'Email could not be sent. Please try again later.',
        })
        return false
      }
    } catch (e) {
      console.warn('Error:', e)
    }
  }

  const getVerification = async () => {
    try {
      const response = await isClientEmailVerified()
      const {
        data: { getClientEmailVerified },
      } = await response.json()
      dispatch(
        updateLoggedInUser({
          isVerified: getClientEmailVerified.success,
        }),
      )
      authorization.updateUser({
        ...user,
        isVerified: getClientEmailVerified.success,
      })
      return getClientEmailVerified
    } catch (e) {
      console.warn('Error:', e)
    }
  }

  useEffect(() => {
    if (user && user.client && user.client.id) {
      dispatch(getClientById(user.client.id))
    }
  }, [])

  useEffect(() => {
    if (!loggedUser || !loggedUser.id) {
      dispatch(updateLoggedInUser(user))
      if (authorization.isClientRole()) {
        getVerification()
      }
    }
  }, [loggedUser])

  useEffect(() => {
    if (isVerified) return

    const isClientVerifiedInterval = setInterval(async () => {
      try {
        const { success } = await getVerification()
        if (success) {
          runSnackBar({
            type: 'success',
            vertical: 'top',
            msg: 'Email verified successfully. You can now save your campaign.',
            autoHideDuration: 6000,
            key: new Date().getTime(),
          })
          clearInterval(isClientVerifiedInterval)
        }
      } catch (e) {
        console.warn('Error:', e)
      }
    }, 10000)

    return () => {
      clearInterval(isClientVerifiedInterval)
    }
  }, [isVerified])

  useEffect(() => {
    if (userId) {
      gtagWrapper({ event: 'ss_user_role', role })
      if (userId && posthog) {
        posthog.identify(userId, {
          role,
          client_id: client ? client.id : '',
          user_id: userId,
          firstname: firstName,
          lastname: lastName,
          country: countryName,
          email,
          phone: mobilePhone,
          company_name: client ? client.name : '',
        })
      }
    }
  }, [userId])

  const isNotLogged = !authorization.isLogged()

  const content = isNotLogged ? null : isNewUI ? (
    smallScreen ? (
      <ActionSheetNavbarSelfServe
        history={history}
        user={user}
        userName={firstName}
        clientName={client.name}
        img={profilePicture}
        email={email}
        sendVerificationEmail={sendVerificationEmail}
        verified={isVerified}
        isOpenAssistant={isOpenAssistant}
        onClose={onClose}
        toggleAssistant={toggleAssistant}
      >
        <DashboardRouter isNewUI={isNewUI} match={match.url} />
      </ActionSheetNavbarSelfServe>
    ) : (
      <HeaderProvider
        isVisible={isHeaderVisible}
        setIsVisible={setIsHeaderVisible}
      >
        <Fragment>
          {isHeaderVisible && (
            <HeaderSelfServe
              userName={firstName}
              clientName={client.name}
              img={profilePicture}
            />
          )}
          <SidebarSelfServe history={history} user={user}>
            <If condition={!isVerified}>
              <VerifyEmail
                email={email}
                sendVerificationEmail={sendVerificationEmail}
              />
            </If>
            <DashboardRouter isNewUI={isNewUI} match={match.url} />
          </SidebarSelfServe>
        </Fragment>
      </HeaderProvider>
    )
  ) : (
    <Fragment>
      <HeaderPlatform />
      <SidebarPlatform match={match.url}>
        <DashboardRouter isNewUI={isNewUI} match={match.url} />
      </SidebarPlatform>
    </Fragment>
  )

  return (
    <div style={{ width: '100%' }}>
      <UIBlock>
        {/* Global iframe preloader for performance optimization */}
        <IframePreloader />

        {content}
        <ReduxModal />
      </UIBlock>
    </div>
  )
}

export default App
